package com.milta.feature.auth.presentation

import com.milta.core.mvi.MviViewModel
import com.milta.feature.auth.data.remote.ApiException
import com.milta.feature.auth.domain.repository.AuthRepository
import kotlinx.coroutines.channels.Channel
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.receiveAsFlow
import kotlinx.coroutines.launch

/** Longueur du code OTP SMS (règle métier — 6 chiffres, MIL-14). Source unique pour l'écran et le ViewModel. */
const val OTP_LENGTH = 6

class OtpViewModel(
    private val phone: String,
    private val repository: AuthRepository,
) : MviViewModel<OtpState, OtpIntent>(OtpState()) {

    private val _navEvent = Channel<OtpNavEvent>(Channel.BUFFERED)
    val navEvent = _navEvent.receiveAsFlow()

    private var resendCount = 0

    init {
        startResendTimer()
    }

    override fun onIntent(intent: OtpIntent) {
        when (intent) {
            is OtpIntent.UpdateCode -> {
                setState { copy(code = intent.value, error = null) }
                if (intent.value.length == OTP_LENGTH) verify(intent.value)
            }
            OtpIntent.Verify -> verify(currentState.code)
            OtpIntent.Resend -> resend()
            OtpIntent.ClearError -> setState { copy(error = null) }
        }
    }

    private fun verify(code: String) {
        if (code.length != OTP_LENGTH) return
        scope.launch {
            setState { copy(isLoading = true, error = null) }
            repository.verifyOtp(phone, code).fold(
                onSuccess = { role ->
                    setState { copy(isLoading = false) }
                    _navEvent.send(OtpNavEvent.NavigateToHome(role))
                },
                onFailure = { e ->
                    val newAttempts = currentState.attempts + 1
                    // The backend returns 401 for all OTP failures.
                    // 429 is returned by the rate-limiter middleware (OWASP / API contract §5.2).
                    // error.otp.expired is surfaced via ApiException.message (machine-readable code
                    // from extensions.code in the GlobalExceptionHandler response body).
                    val errorCode = when {
                        e is ApiException && e.statusCode == 429 -> "otp_too_many_attempts"
                        e is ApiException && e.message == "error.otp.expired" -> "otp_expired"
                        else -> "otp_invalid"
                    }
                    setState { copy(isLoading = false, error = errorCode, attempts = newAttempts, code = "") }
                    if (newAttempts >= 3) {
                        _navEvent.send(OtpNavEvent.NavigateBack)
                    }
                },
            )
        }
    }

    private fun resend() {
        if (currentState.resendCountdown > 0) return
        resendCount++
        if (resendCount >= 5) {
            setState { copy(isResendBlocked = true, error = "otp_resend_limit") }
            return
        }
        scope.launch {
            repository.requestOtp(phone)
            startResendTimer()
        }
    }

    private fun startResendTimer() {
        scope.launch {
            for (i in 60 downTo 0) {
                setState { copy(resendCountdown = i) }
                if (i > 0) delay(1000)
            }
        }
    }
}
